import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { css } from '@emotion/core';
import { ButtonLabel } from '../button/Button';
import { theme } from '../../../styles/theme';
import ArrowIcon from '../../../assets/icons/actions/arrow.svg';
import PhoneIcon from '../../../assets/icons/actions/phone.svg';
import EmailIcon from '../../../assets/icons/actions/email.svg';
import DownloadIcon from '../../../assets/icons/actions/download.svg';
import ExternalLinkIcon from '../../../assets/icons/actions/external-link.svg';
import { ICON_GAP_TRANSITION } from '../../../styles/animation/iconGapTransition';
import { RouterLink } from '../link/RouterLink';

export type LinkType =
	| 'email'
	| 'external'
	| 'internal'
	| 'asset'
	| 'phone'
	| 'externalSecondary';

interface IQuickLinkProps {
	type: LinkType;
	url?: string;
	ariaLabel?: string;
	children?: ReactNode;
	className?: string;
}

export function QuickLink({
	type,
	url,
	ariaLabel,
	children,
	className,
}: IQuickLinkProps) {
	const ICON_MAP = {
		external: <ExternalLinkIcon aria-hidden />,
		email: <EmailIcon aria-hidden />,
		asset: <DownloadIcon aria-hidden />,
		internal: <ArrowIcon aria-hidden />,
		phone: <PhoneIcon aria-hidden />,
		externalSecondary: null,
	};

	const icon = ICON_MAP[type];

	// Necessary for clickable cards, which already have an anchor tag.
	// Rendering RouterLink inside an anchor tag creates a hydration error.
	if (!url)
		return (
			<StyledLinkNoUrl className={className} aria-label={ariaLabel}>
				{children && <ButtonLabel>{children}</ButtonLabel>}
				{icon}
			</StyledLinkNoUrl>
		);

	const openInNewTab = [
		'external',
		'asset',
		'email',
		'externalSecondary',
		'phone',
	].includes(type);

	return (
		<StyledLink
			className={className}
			href={url}
			target={openInNewTab ? '_blank' : undefined}
		>
			<StyledButtonLabel>{children}</StyledButtonLabel>
			{icon}
		</StyledLink>
	);
}

const LinkStyle = css`
	display: flex;
	align-items: center;
	text-decoration: none;
	color: ${theme.typography?.link?.color};
	font-weight: 700;
	gap: ${theme.button?.iconSeparator};
	margin-top: auto;

	svg {
		transition: ${`transform ${ICON_GAP_TRANSITION}`};
		min-width: fit-content;
	}

	&:hover {
		svg {
			transform: translateX(${theme.button?.iconSeparator});
		}
	}
`;

const StyledLink = styled(RouterLink)`
	${LinkStyle}
`;

const StyledLinkNoUrl = styled.span`
	${LinkStyle}
`;

const StyledButtonLabel = styled(ButtonLabel)`
	max-width: none;
	white-space: normal;
	text-overflow: clip;
	overflow: visible;
`;
